import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { brands } from '../data/brands';

// ============================================
// GLOBAL MOTION CONSTANTS (same as BrandDetailPage)
// ============================================
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DURATION = {
  fast: 280,
  normal: 650,
  slow: 800,
};
const STAGGER = {
  tight: 70,
  normal: 90,
  relaxed: 120,
};

const filterOptions = ['All', 'Licensed', 'In-House'];

// ============================================
// REDUCED MOTION HOOK
// ============================================
const usePrefersReducedMotion = () => {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mediaQuery.matches);

    const handler = (e) => setPrefersReduced(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReduced;
};

// ============================================
// SCROLL REVEAL HOOK (IntersectionObserver)
// ============================================
const useScrollReveal = (options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const prefersReduced = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReduced]);

  return [ref, isVisible];
};

// ============================================
// BRAND CARD COMPONENT
// ============================================
const BrandCard = ({ brand, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [ref, isVisible] = useScrollReveal();
  const prefersReduced = usePrefersReducedMotion();

  const accentColor = brand.accentColor || '#ffffff';

  // Stagger delay based on position in grid (resets per row of 3)
  const delay = (index % 3) * STAGGER.normal;

  return (
    <Link
      ref={ref}
      to={`/brands/${brand.slug}`}
      className="block relative aspect-[4/5] rounded-xl overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? isHovered && !prefersReduced
            ? 'translateY(-6px) scale(1.01)'
            : 'translateY(0) scale(1)'
          : 'translateY(12px) scale(0.98)',
        filter: isVisible ? 'blur(0px)' : 'blur(4px)',
        transition: `
          opacity ${DURATION.normal}ms ${EASING} ${delay}ms,
          transform ${DURATION.normal}ms ${EASING} ${delay}ms,
          filter ${DURATION.normal}ms ${EASING} ${delay}ms
        `,
      }}
    >
      {/* Cover Image with slow zoom on hover */}
      <div
        className="absolute inset-0"
        style={{
          transform: isHovered && !prefersReduced ? 'scale(1.04)' : 'scale(1)',
          transition: `transform ${DURATION.slow}ms ${EASING}`,
        }}
      >
        <img
          src={brand.coverImage}
          alt={brand.name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Gradient Overlay - intensifies on hover */}
      <div
        className="absolute inset-0"
        style={{
          background: isHovered
            ? 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.88) 100%)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0.72) 100%)',
          transition: `background ${DURATION.fast}ms ${EASING}`,
        }}
      />

      {/* Type Label with accent styling on hover */}
      <span
        className="absolute top-4 left-4 text-[9px] uppercase tracking-[0.14em] font-medium px-2.5 py-1 rounded-full"
        style={{
          color: isHovered ? accentColor : 'rgba(255,255,255,0.5)',
          background: isHovered ? `${accentColor}12` : 'transparent',
          border: isHovered ? `1px solid ${accentColor}28` : '1px solid transparent',
          transition: `all ${DURATION.fast}ms ${EASING}`,
        }}
      >
        {brand.type === 'licensed' ? 'Licensed' : 'In-House'}
      </span>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3
          className="text-white text-[19px] font-medium tracking-tight mb-1"
          style={{
            transform: isHovered && !prefersReduced ? 'translateY(-2px)' : 'translateY(0)',
            transition: `transform ${DURATION.fast}ms ${EASING}`,
          }}
        >
          {brand.name}
        </h3>
        <p className="text-white/45 text-[12px] tracking-wide mb-0">
          {brand.genderProgram}
        </p>

        {/* Hover-reveal content */}
        <div
          className="overflow-hidden"
          style={{
            maxHeight: isHovered ? '90px' : '0px',
            opacity: isHovered ? 1 : 0,
            marginTop: isHovered ? '12px' : '0px',
            transition: `
              max-height ${DURATION.normal}ms ${EASING},
              opacity ${DURATION.fast}ms ${EASING},
              margin-top ${DURATION.fast}ms ${EASING}
            `,
          }}
        >
          <div
            className="pt-3"
            style={{ borderTop: `1px solid ${accentColor}25` }}
          >
            <p className="text-white/55 text-[13px] leading-relaxed mb-2.5 line-clamp-2">
              {brand.hero.headline}
            </p>
            <span
              className="text-[11px] font-medium tracking-wide inline-flex items-center gap-1"
              style={{
                color: accentColor,
                transform: isHovered && !prefersReduced ? 'translateX(4px)' : 'translateX(0)',
                transition: `transform ${DURATION.fast}ms ${EASING}`,
              }}
            >
              View Brand
              <span
                style={{
                  display: 'inline-block',
                  transform: isHovered && !prefersReduced ? 'translateX(2px)' : 'translateX(0)',
                  transition: `transform ${DURATION.fast}ms ${EASING} 50ms`,
                }}
              >
                →
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Border with accent glow on hover */}
      <div
        className="absolute inset-0 rounded-xl pointer-events-none"
        style={{
          border: isHovered ? `1px solid ${accentColor}35` : '1px solid rgba(255,255,255,0.05)',
          boxShadow: isHovered
            ? `0 18px 40px rgba(0,0,0,0.35), 0 0 30px ${accentColor}10, inset 0 0 0 1px ${accentColor}08`
            : '0 4px 20px rgba(0,0,0,0.15)',
          transition: `all ${DURATION.fast}ms ${EASING}`,
        }}
      />
    </Link>
  );
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const BrandsPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [headerVisible, setHeaderVisible] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  // Preload all brand card images immediately
  useEffect(() => {
    brands.forEach((brand) => {
      const img = new Image();
      img.src = brand.coverImage;
    });
  }, []);

  useEffect(() => {
    if (prefersReduced) {
      setHeaderVisible(true);
      return;
    }
    const timer = setTimeout(() => setHeaderVisible(true), 100);
    return () => clearTimeout(timer);
  }, [prefersReduced]);

  const filteredBrands = activeFilter === 'All'
    ? brands
    : activeFilter === 'Licensed'
      ? brands.filter(brand => brand.type === 'licensed')
      : brands.filter(brand => brand.type === 'in_house');

  return (
    <main className="bg-[#0a0a0a] min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[1320px] mx-auto">
        {/* Header with entrance animation */}
        <div
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(16px)',
            transition: `all ${DURATION.normal}ms ${EASING}`,
          }}
        >
          <p className="text-[#6a6a6a] text-[12px] mb-3.5 uppercase tracking-[0.18em]">Our Portfolio</p>
          <h1 className="text-[46px] md:text-[60px] font-extralight text-white mb-6 tracking-[-0.025em]">
            LV1 Brands
          </h1>
          <p className="text-[#8a8a8a] text-[16px] leading-[1.75] max-w-[560px] mx-auto">
            We partner with brands that have strong identity, clear vision, and the potential to win at retail.
          </p>
        </div>

        {/* Filter with entrance animation */}
        <div
          className="flex flex-wrap justify-center gap-2 mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(12px)',
            transition: `all ${DURATION.normal}ms ${EASING} 80ms`,
          }}
        >
          {filterOptions.map((filter) => (
            <FilterButton
              key={filter}
              filter={filter}
              isActive={activeFilter === filter}
              onClick={() => setActiveFilter(filter)}
            />
          ))}
        </div>

        {/* Brands Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredBrands.map((brand, index) => (
            <BrandCard key={brand.slug} brand={brand} index={index} />
          ))}
        </div>

        {/* CTA Section */}
        <CTASection headerVisible={headerVisible} />
      </div>
    </main>
  );
};

// ============================================
// FILTER BUTTON COMPONENT
// ============================================
const FilterButton = ({ filter, isActive, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="px-5 py-2.5 rounded-full text-[13px] font-medium"
      style={{
        background: isActive ? 'white' : 'rgba(255,255,255,0.04)',
        color: isActive ? '#0a0a0a' : 'rgba(255,255,255,0.65)',
        border: isActive ? '1px solid white' : '1px solid rgba(255,255,255,0.08)',
        transform: isHovered && !isActive && !prefersReduced ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: isActive ? '0 4px 12px rgba(255,255,255,0.15)' : 'none',
        transition: `all ${DURATION.fast}ms ${EASING}`,
      }}
    >
      {filter}
    </button>
  );
};

// ============================================
// CTA SECTION COMPONENT
// ============================================
const CTASection = ({ headerVisible }) => {
  const [ref, isVisible] = useScrollReveal();
  const [isHovered, setIsHovered] = useState(false);
  const prefersReduced = usePrefersReducedMotion();

  return (
    <div
      ref={ref}
      className="text-center mt-20"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
        transition: `all ${DURATION.normal}ms ${EASING}`,
      }}
    >
      <div
        className="p-10 rounded-[20px] inline-block"
        style={{
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(255, 255, 255, 0.018)',
          boxShadow: `
            inset 0 0 18px rgba(255,255,255,0.025),
            inset 0 1px 1px rgba(255,255,255,0.06),
            0px 4px 20px rgba(0,0,0,0.1)
          `,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          borderLeft: '1px solid rgba(255,255,255,0.06)',
          borderRight: '1px solid rgba(255,255,255,0.03)',
          borderBottom: '1px solid rgba(255,255,255,0.02)',
        }}
      >
        <h3 className="text-white text-[23px] font-light mb-4 tracking-[-0.01em]">
          Interested in partnering with LV1?
        </h3>
        <p className="text-[#8a8a8a] text-[15px] mb-6 max-w-[380px] leading-[1.7]">
          We are always looking for brands with strong identity and retail potential.
        </p>
        <Link
          to="/contact"
          className="inline-flex items-center px-7 py-3.5 bg-white text-[#0a0a0a] rounded-full text-[14px] font-medium"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            transform: isHovered && !prefersReduced ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: isHovered
              ? '0 8px 25px rgba(255,255,255,0.2)'
              : '0 4px 15px rgba(255,255,255,0.1)',
            transition: `all ${DURATION.fast}ms ${EASING}`,
          }}
        >
          Get In Touch
        </Link>
      </div>
    </div>
  );
};

export default BrandsPage;
